import { Component, Output, EventEmitter, OnInit, OnDestroy, HostBinding } from '@angular/core';
import { NavigationEnd, RouterLink, RouterLinkActive } from '@angular/router';
import { RouterOutlet } from '@angular/router';
import { NgClass, CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Subject, Subscription, combineLatest, of } from 'rxjs';
import { takeUntil, filter, take, shareReplay, switchMap, tap } from 'rxjs/operators';
import { FirestoreService } from '../services/firestore';
import { AuthService } from '../auth.service'; // Importar AuthService
import { DataService } from '../services/data';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { MenubarModule } from 'primeng/menubar';
import { PanelMenuModule } from 'primeng/panelmenu';
import { Menu } from 'primeng/menu';
import { BadgeModule } from 'primeng/badge';
import { MenuItem } from 'primeng/api';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { User } from '../models/user.model';
import { ToastService } from '../services/toast.service';
import { Timestamp } from 'firebase/firestore';
import { MemberService } from '../services/member.service';
import { FamiliesService } from '../services/familes.service';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CurrentUserService } from '../services/current-user.service';
import { resolveLoggedInDisplayName, resolvePersonName } from '../helpers/user-display-name.helper';
import { LanguageSelectorComponent } from '../components/language-selector/language-selector';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SupportService } from '../services/support.service';
import {
  countUnreadSupportTickets,
  supportReadChanged$,
} from '../support/support-unread';
import { SupportTicket } from '../models/support-ticket.model';
import { NavId, VisibleNavGroup, hasAnyRole, matchesRoute, visibleNavGroups, visibleNavItems } from '../navigation/app-navigation';
import { HelpCenterComponent } from '../help/help-center.component';

interface SidebarItem {
  id: NavId;
  /** Clave ngx-translate (SIDENAV.*) */
  label: string;
  icon: [string, string];
  primeIcon: string;
  route: string;
  allowedRoles: readonly string[];
}

interface MobileMenuItem extends MenuItem {
  isSupport?: boolean;
}

@Component({
  selector: 'app-sidenav',
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NgClass,
    FontAwesomeModule,
    MenubarModule,
    PanelMenuModule,
    BadgeModule,
    ButtonModule,
    LanguageSelectorComponent,
    TranslateModule,
    HelpCenterComponent,
  ],
  templateUrl: './sidenav.html',
  styleUrls: ['./sidenav.css']
})
export class Sidenav implements OnInit, OnDestroy {
  @Output() sidenavToggle = new EventEmitter<boolean>();
  private destroy$ = new Subject<void>();

  selectedMonth: Date | null = null;

  private subscriptions = new Subscription();
  private churchSessionSubs = new Subscription();
  private membersSub?: Subscription;
  private familiesSub?: Subscription;
  private currentUserSub?: Subscription;

  isExpanded: boolean = false;
  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;


  profileMenu: MenuItem[] = [
    {
      label: 'SIDENAV.GO_PROFILE',
      icon: 'pi pi-user',
      command: () => this.router.navigate(['/dashboard/profile']),
      routerLink: ['profile']
    },
    {
      label: 'SIDENAV.SIGN_OUT',
      icon: 'pi pi-sign-out',
      command: () => this.logout()
    }
  ];

  profileMenuMobile: MenuItem[] = [];

  /**
   * Respaldo: el menú lateral sigue en la plantilla y en el CSS. Poner a `true` para volver a mostrarlo
   * (en pantallas de 1400 px o más) en lugar del menú por secciones de la barra superior.
   */
  readonly useSidebarLayout = false;

  @HostBinding('class.layout-sidebar') get layoutSidebarClass(): boolean {
    return this.useSidebarLayout;
  }

  navGroups: VisibleNavGroup[] = [];
  currentUrl = '';

  sidebarItems: SidebarItem[] = [];
  userRoles: string[] = [];
  churchId: string = '';
  churchName: string = '';
  displayName: string = '';
  photoURL: string = '';

  prevYM: string = '';
  showBanner: boolean = false;
  supportAccess = false;
  exitingSupport = false;
  supportUnreadCount = 0;
  /** Visible si flag de misión O flag de esta iglesia. */
  tutorialesHabilitados = false;
  private tutorialesPorIglesia = false;
  private tutorialesPorMision = false;
  private openSupportTickets: SupportTicket[] = [];
  private supportTicketsSub?: Subscription;

  uidCurrentUser: string = '';
  accessibleChurches: any[] = [];
  private authDisplayName = '';
  private didBackfillAuthDisplayName = false;

  constructor(
    private firestoreService: FirestoreService,
    private dataService: DataService,
    private authService: AuthService,
    private toastService: ToastService,
    private memberService: MemberService,
    private currentUserService: CurrentUserService,
    private familyService: FamiliesService,
    private supportService: SupportService,
    private translate: TranslateService,
    public router: Router
  ) { }

  ngOnInit(): void {
    const { currYM, prevYM } = this.firestoreService.getCurrentAndPreviousYM();
    this.currentUrl = this.router.url;
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(e => {
      this.currentUrl = e.urlAfterRedirects;
    });
    // Suscribirse a los roles del usuario desde DataService
    this.dataService.userRoles$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(roles => {
      this.userRoles = roles;
      this.inicializarMenus(); // Inicializar menús cuando los roles cambian
      this.actualizarProfileMenuMobile();
    });

    this.dataService.supportAccess$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(active => {
      this.supportAccess = !!active;
    });

    this.dataService.habilitarTutoriales$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(enabled => {
      this.tutorialesPorMision = enabled === true;
      this.refreshTutorialesMenuVisibility();
    });

    // Suscribirse al churchId desde DataService
    this.dataService.churchId$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(churchId => {
      this.teardownChurchSession();
      if (!churchId) {
        this.churchId = '';
        return;
      }

      this.churchId = churchId;
      this.loadChurchData(churchId);
      this.loadIglesiaData(churchId);
      this.loadEvents(churchId);
      this.cargarMiembros(churchId);
      this.loadPreInforme(churchId);
      this.getClaimsUserSidenav();
      this.dataService.updateOpenPeriodo(null);
      this.bindSupportUnread(churchId);

      this.churchSessionSubs.add(
        supportReadChanged$.subscribe(() => this.refreshSupportUnreadCount())
      );

      this.churchSessionSubs.add(
        this.firestoreService.observeChurchSignature(churchId).subscribe()
      );

      this.churchSessionSubs.add(
        this.dataService.dataChurch$.pipe(
          filter(data => !!data)
        ).subscribe(data => {
          this.churchName = data?.churchName || '';
          this.tutorialesPorIglesia = data?.tutorialesHabilitados === true;
          this.refreshTutorialesMenuVisibility();
        })
      );

      this.churchSessionSubs.add(
        this.firestoreService.getPeriodosFinancierosState(churchId).pipe(
          switchMap(state => {
            const periodo = state.oldestYm
              ? ({ id: state.oldestYm, ym: state.oldestYm, status: 'abierto' as const })
              : null;
            this.dataService.updateOpenPeriodo(periodo);
            this.dataService.updatePeriodosFinancierosState(state);

            const periodoId = periodo?.id ?? '';
            const isSameAsCurrent = periodoId !== '' && periodoId === currYM;

            this.showBanner = !!periodo && periodoId !== '' && !isSameAsCurrent;

            this.prevYM = prevYM;
            this.dataService.updateBanner(this.showBanner);

            return this.loadFinancialData$(churchId, periodo?.id || '');
          })
        ).subscribe()
      );
    });

    this.dataService.accessibleChurches$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(churches => {
      this.accessibleChurches = churches || [];

      this.accessibleChurches.forEach(c => {
        if (c.name === 'Cargando...') {
          this.firestoreService.getIglesia(c.id).pipe(take(1)).subscribe((data: any) => {
            if (data) {
              c.name = data.nombre || data.churchName || 'Iglesia';
            }
          });
        }
      });

      const hasMultiple = churches && churches.length > 1;
      this.updateProfileMenus(hasMultiple);
    });

    this.suscripciones();
  }
  suscripciones() {
    this.subscriptions.add(
      this.dataService.photoUser$.subscribe(url => {
        this.photoURL = url;
      })
    );
    this.subscriptions.add(
      this.dataService.displayName$.subscribe(name => {
        this.displayName = name;
      })
    );
  }

  private teardownChurchSession(): void {
    this.churchSessionSubs.unsubscribe();
    this.churchSessionSubs = new Subscription();
    this.membersSub?.unsubscribe();
    this.membersSub = undefined;
    this.familiesSub?.unsubscribe();
    this.familiesSub = undefined;
    this.currentUserSub?.unsubscribe();
    this.currentUserSub = undefined;
    this.supportTicketsSub?.unsubscribe();
    this.supportTicketsSub = undefined;
    this.supportUnreadCount = 0;
    this.openSupportTickets = [];
    this.tutorialesPorIglesia = false;
    this.tutorialesHabilitados = this.tutorialesPorMision;
    this.memberService.stopListening();
    this.familyService.stopListening();
    this.currentUserService.unsubscribeCurrentUser();
  }

  private refreshTutorialesMenuVisibility(): void {
    this.tutorialesHabilitados = this.tutorialesPorMision || this.tutorialesPorIglesia;
    this.inicializarMenus();
    this.actualizarProfileMenuMobile();
  }

  private loadIglesiaData(churchId: string) {
    this.churchSessionSubs.add(
      this.firestoreService.getIglesia(churchId).subscribe({
        next: (data) => {
          this.dataService.setDataChurch(data);
          this.dataService.isDataChurch = true;
          this.dataService.startNewIglesia = data?.startNew || false;
        },
        error: () => {
          this.toastService.showError('Error al cargar iglesia.');
        }
      })
    );
  }

  loadPreInforme(churchId: string) {
    if (!churchId) {
      return;
    }
    this.churchSessionSubs.add(
      this.firestoreService.getPreInforme(churchId).subscribe({
        next: (data) => {
          this.dataService.updatePreInforme(data);
        },
        error: () => {
          this.toastService.showError('Error al cargar PreInforme.');
        }
      })
    );
  }

  cargarMiembros(churchId: string) {
    if (!churchId) {
      return;
    }

    this.membersSub = this.memberService
      .loadAllMembersForChurch(churchId)
      .subscribe({ next: () => { }, error: () => { } });
    this.churchSessionSubs.add(this.membersSub);

    this.familiesSub = this.familyService
      .loadAllFamiliesForChurch(churchId)
      .subscribe({ next: () => { }, error: () => { } });
    this.churchSessionSubs.add(this.familiesSub);
  }

  private getDataUser(churchId: string) {
    if (!churchId || !this.uidCurrentUser) {
      return;
    }

    this.currentUserSub = this.currentUserService
      .listenForCurrentUser(this.churchId, this.uidCurrentUser)
      .subscribe({
        next: (localUser) => this.maybeBackfillAuthDisplayName(localUser),
        error: () => { }
      });
    this.churchSessionSubs.add(this.currentUserSub);
  }

  private maybeBackfillAuthDisplayName(localUser: User | null): void {
    if (this.didBackfillAuthDisplayName || this.authDisplayName || !this.uidCurrentUser) {
      return;
    }
    const fromLocal = resolvePersonName(localUser);
    if (!fromLocal) return;

    this.didBackfillAuthDisplayName = true;
    this.dataService.setCurrentDisplayName(fromLocal);

    const updateUserProfile = httpsCallable(getFunctions(), 'updateUserProfile');
    updateUserProfile({ uid: this.uidCurrentUser, displayName: fromLocal }).catch(() => { });
  }

  private async getClaimsUserSidenav() {
    const user = await this.authService.getCurrentUserAsync();

    if (user) {
      this.uidCurrentUser = user.uid;
      this.authDisplayName = (user.displayName || '').trim();
      this.didBackfillAuthDisplayName = false;

      const initialName = resolveLoggedInDisplayName({
        authDisplayName: this.authDisplayName,
        email: user.email || '',
      });
      this.displayName = initialName;
      this.dataService.setCurrentDisplayName(initialName);
      this.dataService.setUidUser(user.uid);
      this.dataService.setCurrentPhotoUser(user.photoURL || '');
      this.getDataUser(this.churchId);
    }
  }

  async loadEvents(churchId: string): Promise<void> {
    if (!churchId) {
      return;
    }
    try {
      const functions = getFunctions();
      const getEvents = httpsCallable(functions, 'getEvents');
      getEvents({ churchId })
        .then(resp => {
          const result = resp.data as any;

          if (result.success) {
            const eventos = result.events.map((event: any) => ({
              id: event.id,
              title: event.title,
              start: event.start,
              date: event.date?._seconds
                ? Timestamp.fromMillis(event.date._seconds * 1000).toDate()
                : new Date(),
              description: event.description,
              icon: event.icon,
              allDay: event.allDay,
              color: event.color
            }));

            this.dataService.updateEvents(eventos);

            // 🚨 Aquí te suscribes en tiempo real
            this.dataService.subscribeToEventsRealtime(churchId);
          }
        })
        .catch(error => {
        })
    } catch (error: any) {
      this.toastService.showError('Error al cargar eventos:');
    }
  }

  private loadChurchData(churchId: string): void {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth();

    if (month === 0) {
      month = 11;
      year--;
    } else {
      month--;
    }

    const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
    const previousMonthName = monthNames[month];
    this.dataService.updatePreviousMonthName(previousMonthName);

    this.firestoreService.getPreviousMonthSummary(churchId, year, month + 1);
  }

  private loadFinancialData$(churchId: string, periodo: string) {
    const base = (() => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), 1); })();
    const currentYear = String(base.getFullYear());
    const currentMonth = String(base.getMonth() + 1).padStart(2, '0');

    if (!periodo || !periodo.includes('-')) {
      return of([] as any[]);
    }

    const [year, month] = periodo.split('-');
    const monthName = this.obtenerNombreMesYAno(month, year);
    this.dataService.updatePreviousMonthName(monthName);

    const currentMovs$ = this.firestoreService
      .getMovimientos(churchId, currentYear, currentMonth)
      .pipe(shareReplay(1));

    if (!this.showBanner) {
      return currentMovs$.pipe(
        tap(movs => this.dataService.updateOfferings(movs))
      );
    }

    const prevMovs$ = this.firestoreService.getMovimientosPrevYM(
      churchId,
      String(year),
      String(month).padStart(2, '0')
    );

    return combineLatest([currentMovs$, prevMovs$]).pipe(
      tap(([movs, prevMovs]) => {
        this.dataService.updateOfferings(movs);
        this.dataService.updateOfferingsPrevMonth(prevMovs);
      })
    );
  }

  inicializarMenus() {
    const navCtx = { roles: this.userRoles, tutorialsEnabled: this.tutorialesHabilitados };
    this.navGroups = visibleNavGroups(navCtx);
    this.sidebarItems = visibleNavItems(navCtx).map(item => ({
      id: item.id,
      label: item.labelKey,
      icon: item.faIcon,
      primeIcon: item.primeIcon,
      route: item.route,
      allowedRoles: item.allowedRoles,
    }));
    this.actualizarProfileMenuMobile();
  }

  updateProfileMenus(hasMultipleChurches: boolean) {
    this.profileMenu = [
      {
        label: 'SIDENAV.GO_PROFILE',
        icon: 'pi pi-user',
        command: () => this.router.navigate(['/dashboard/profile']),
        routerLink: ['profile']
      }
    ];

    if (hasMultipleChurches) {
      this.profileMenu.push({
        label: 'SIDENAV.SWITCH_CHURCH',
        icon: 'pi pi-arrow-right-arrow-left',
        command: () => this.router.navigate(['/select-church'])
      });
    }

    this.profileMenu.push({
      label: 'SIDENAV.SIGN_OUT',
      icon: 'pi pi-sign-out',
      command: () => this.logout()
    });

    this.actualizarProfileMenuMobile(hasMultipleChurches);
  }

  private actualizarProfileMenuMobile(hasMultipleChurches: boolean = false): void {
    this.profileMenuMobile = [
      {
        label: 'SIDENAV.PROFILE',
        icon: 'pi pi-user',
        command: () => this.router.navigate(['/dashboard/profile'])
      }
    ];

    if (hasMultipleChurches) {
      this.profileMenuMobile.push({
        label: 'SIDENAV.SWITCH_CHURCH',
        icon: 'pi pi-arrow-right-arrow-left',
        command: () => this.router.navigate(['/select-church'])
      });
    }

    this.profileMenuMobile.push({ separator: true });

    this.profileMenuMobile.push(
      ...this.sidebarItems
        .filter(item => this.hasAccess(item.allowedRoles))
        .map(item => ({
          label: item.label,
          icon: item.primeIcon,
          command: () => this.router.navigate([item.route])
        }))
    );

    this.profileMenuMobile.push({ separator: true });

    this.profileMenuMobile.push({
      label: 'SIDENAV.SIGN_OUT',
      icon: 'pi pi-sign-out',
      command: () => this.logout()
    }
    );
    this.profileMenuMobile = [...this.profileMenuMobile];
  }

  hasAccess(allowedRoles: readonly string[]): boolean {
    return hasAnyRole(this.userRoles, allowedRoles ?? []);
  }

  status = true;
  addToggle() {
    this.status = !this.status;
  }

  handleMouseEnter() {
    this.status = false;
  }

  handleMouseLeave() {
    this.status = true;
  }

  async logout(): Promise<void> {
    await this.authService.logout();
  }

  switchChurch(churchId: string) {
    if (churchId === this.churchId) return;
    this.dataService.switchChurch(churchId);
    // Evitar rutas de detalle con IDs de la iglesia anterior.
    this.router.navigate(['/dashboard/main']);
  }

  private bindSupportUnread(churchId: string): void {
    this.supportTicketsSub?.unsubscribe();
    this.supportTicketsSub = this.supportService.listenOpenTickets(churchId).subscribe({
      next: (tickets) => {
        this.openSupportTickets = tickets || [];
        this.refreshSupportUnreadCount();
      },
      error: () => {
        this.openSupportTickets = [];
        this.supportUnreadCount = 0;
      },
    });
    this.churchSessionSubs.add(this.supportTicketsSub);
  }

  private refreshSupportUnreadCount(): void {
    this.supportUnreadCount = countUnreadSupportTickets(
      this.openSupportTickets,
      this.uidCurrentUser
    );
  }

  /** Activa también en subrutas (p. ej. la ficha de un miembro o la vista previa del informe). */
  isNavItemActive(route: string): boolean {
    const url = this.currentUrl.split(/[?#]/)[0];
    if (matchesRoute(url, route) || url.startsWith(route + '/')) return true;
    return route === '/dashboard/reports' && /^\/dashboard\/(preview-report|reports-old)\b/.test(url);
  }

  isNavGroupActive(group: VisibleNavGroup): boolean {
    return group.items.some(item => this.isNavItemActive(item.route));
  }

  groupHasSupportUnread(group: VisibleNavGroup): boolean {
    return this.supportUnreadCount > 0 && group.items.some(item => item.id === 'support');
  }

  isSupportMenuItem(item: SidebarItem | MobileMenuItem | MenuItem): boolean {
    const route = (item as SidebarItem).route ?? (item as MenuItem).routerLink?.toString() ?? '';
    return route.includes('support') || (item as MobileMenuItem).isSupport === true;
  }

  async exitSupportChurch(): Promise<void> {
    if (this.exitingSupport) return;
    this.exitingSupport = true;
    try {
      await this.supportService.exitChurchAsSupport();
      this.toastService.showSuccess(this.translate.instant('SIDENAV.EXIT_CHURCH'));
      await this.router.navigate(['/select-church']);
    } catch {
      this.toastService.showError(this.translate.instant('COMMON.ERROR'));
    } finally {
      this.exitingSupport = false;
    }
  }

  ngOnDestroy(): void {
    this.teardownChurchSession();
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
  }

  obtenerNombreMesYAno(month: string, year: string): string {
    const meses = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    // Convertir a números y validar
    const mesNumero = parseInt(month, 10);
    const anioNumero = parseInt(year, 10);

    // Validaciones básicas
    if (isNaN(mesNumero) || mesNumero < 1 || mesNumero > 12) {
      throw new Error('Mes inválido. Debe ser entre 01 y 12');
    }

    if (isNaN(anioNumero) || anioNumero < 1000 || anioNumero > 9999) {
      throw new Error('Año inválido. Debe ser un año de 4 dígitos');
    }

    // Obtener nombre del mes (restamos 1 porque el array empieza en 0)
    const nombreMes = meses[mesNumero - 1];

    return `${nombreMes}  ${anioNumero}`;
  }

}
